import { Link, useLocation } from 'react-router-dom'
import { ArrowLeft, ListChecks } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { JobChecklistTemplatePanel } from '@/components/jobs/job-checklist-template-panel'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import { useWorkspaceLabels } from '@/contexts/workspace-portal-context'

export default function JobChecklistsPage() {
  const location = useLocation()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  const labels = useWorkspaceLabels()
  const isProjectMode = labels.mode === 'project'
  const isEventMode = labels.mode === 'event'

  return (
    <div className="space-y-4 pb-2">
      <section className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <Badge variant="outline" className="w-fit rounded-md border-blue-200 bg-blue-50 text-blue-700">
            Checklist administration
          </Badge>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-lg font-semibold tracking-tight">
              {isProjectMode ? 'Project checklists' : isEventMode ? 'Event checklists' : 'Job checklists'}
            </h1>
          </div>
          <p className="text-sm text-muted-foreground">
            {isProjectMode
              ? 'Configure reusable SOP templates by project category. New projects and work days copy matching steps automatically.'
              : isEventMode
                ? `Configure reusable SOP templates by event category. New events and ${labels.workDayPluralLower} copy matching steps automatically.`
                : 'Configure reusable SOP templates by job category. New jobs and work days copy matching steps automatically.'}
          </p>
        </div>

        <Button variant="outline" size="sm" className="rounded-lg" asChild>
          <Link to={withServiceInstanceId(labels.basePath, serviceInstanceId)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {`Back to ${labels.entityPluralLower}`}
          </Link>
        </Button>
      </section>

      <Card className="rounded-[28px] border-white/70 bg-card/90 shadow-[0_20px_40px_-35px_rgba(15,23,42,0.55)]">
        <CardHeader className="border-b border-border/60 pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <ListChecks className="h-4 w-4" />
            Checklist templates
          </CardTitle>
          <CardDescription>
            {isProjectMode
              ? 'Templates apply when projects and work days are created. Existing projects keep their own checklist copies.'
              : isEventMode
                ? `Templates apply when events and ${labels.workDayPluralLower} are created. Existing events keep their own checklist copies.`
                : 'Templates apply when jobs and work days are created. Existing jobs keep their own checklist copies.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <JobChecklistTemplatePanel kind={isProjectMode ? 'project' : 'job'} />
        </CardContent>
      </Card>
    </div>
  )
}
